// Pure renderer for a single bingo card — used in preview & PDF
import { patternFor } from "../lib/patterns";
import AutoFitText from "./AutoFitText";

export default function BingoCardRender({ title, size, cells, theme, freeSpace, cardNumber, brandName, logo, logoSize = 48, footerText, code, freeLabel = "FREE", themeId = "classic", showCaptions = true, plainBg = false }) {
  // BINGO letter row only renders on 5x5 cards. 4x4 and 3x3 grids omit the
  // header letters entirely (the bingo theme works equally well without them
  // and saves vertical space for bigger cell content).
  const showLetters = size === 5;
  const letters = ["B", "I", "N", "G", "O"];
  const bgPattern = plainBg ? null : patternFor(themeId);

  // Title shrinks for long strings so it always fits on one line
  const titleStr = (title || "Bingo").toString();
  const titleLen = titleStr.length;
  let titleSize;
  if (titleLen > 26) titleSize = "0.95rem";
  else if (titleLen > 20) titleSize = "1.15rem";
  else if (titleLen > 15) titleSize = "1.45rem";
  else if (titleLen > 10) titleSize = "1.75rem";
  else if (titleLen > 6) titleSize = "2.1rem";
  else titleSize = "2.45rem";

  // Per-grid-size font ranges (px). Conservative max sizes so the AutoFitText
  // algorithm can find a fitting size even for long song titles, and very
  // low min so worst-case it always fits without overflowing.
  const fontRange = size === 3
    ? { max: 60, min: 10 }
    : size === 4
      ? { max: 48, min: 9 }
      : { max: 36, min: 8 };

  const accent = theme.header;
  const isMusic = themeId === "muziek";
  // Inner panel background: light purple for music bingo (festive look),
  // white for all other themes. The pattern overlay adds notes/confetti.
  const innerBg = isMusic ? "#F5ECFF" : "#ffffff";
  // Sinterklaas AI patterns: a tiled image fills the whole card. `titleColor`
  // keeps the header/footer legible on the light pattern; the inner play
  // panel turns semi-transparent so the pattern subtly shows between cells.
  const hasBgImage = !!theme.bgImage;
  const headerTextColor = theme.titleColor || theme.headerText;
  const panelBg = hasBgImage ? "rgba(255,255,255,0.45)" : innerBg;

  return (
    <div
      className="bingo-card-render w-full relative"
      style={{
        backgroundColor: hasBgImage ? (theme.bgFallback || "#FFF5F0") : accent,
        backgroundImage: hasBgImage ? `url("${theme.bgImage}")` : undefined,
        backgroundSize: hasBgImage ? "220px" : undefined,
        backgroundRepeat: hasBgImage ? "repeat" : undefined,
        padding: "14px 22px",
        border: "3px solid #111",
        borderRadius: "12px",
        boxShadow: "4px 4px 0 #111",
      }}
      data-testid={`bingo-card-render${cardNumber ? `-${cardNumber}` : ""}`}
    >
      <div
        className="flex items-stretch gap-3 relative px-2"
        style={{ color: headerTextColor, paddingTop: "0.6em", paddingBottom: "0.85em" }}
      >
        {logo ? (
          <img
            src={logo}
            alt="logo"
            className="object-contain rounded bg-white flex-shrink-0 self-center"
            style={{
              width: `${logoSize}px`,
              height: `${logoSize}px`,
              padding: "4px",
              border: "2px solid rgba(255,255,255,0.4)",
              display: "block",
              boxSizing: "border-box",
            }}
          />
        ) : null}
        <div className="flex-1 min-w-0 flex flex-col justify-center">
          <div
            className="font-display font-black uppercase tracking-tight"
            style={{
              fontSize: titleSize,
              lineHeight: 1.2,
              fontFamily: "'Outfit', 'DM Sans', system-ui, sans-serif",
              fontWeight: 900,
              wordBreak: "break-word",
              overflowWrap: "anywhere",
            }}
            title={titleStr}
          >
            {titleStr}
          </div>
          {brandName ? (
            <div
              className="font-bold uppercase tracking-widest opacity-90"
              style={{
                fontSize: "0.78rem",
                marginTop: "0.25em",
                lineHeight: 1.35,
                fontFamily: "'DM Sans', system-ui, sans-serif",
                wordBreak: "break-word",
                overflowWrap: "anywhere",
                whiteSpace: "normal",
                paddingBottom: "0.1em",
              }}
            >
              {brandName}
            </div>
          ) : null}
        </div>
        {cardNumber ? (
          <div className="font-mono text-xs sm:text-sm font-bold opacity-95 whitespace-nowrap flex-shrink-0 self-center">
            #{cardNumber}{code ? ` · ${code}` : ""}
          </div>
        ) : null}
      </div>

      {/* Inner white panel containing the BINGO letters + cells */}
      <div
        className="relative"
        style={{
          backgroundColor: panelBg,
          backgroundImage: !hasBgImage && bgPattern ? `url("${bgPattern}")` : undefined,
          backgroundSize: !hasBgImage && bgPattern ? (isMusic ? "160px 160px" : "120px 120px") : undefined,
          padding: "10px",
          border: "2px solid #111",
          borderRadius: "8px",
        }}
      >
        {showLetters && (
          <div
            className="grid mb-2 gap-1.5 relative"
            style={{ gridTemplateColumns: `repeat(${size}, minmax(0, 1fr))` }}
          >
            {letters.map((l) => (
              <div
                key={l}
                style={{
                  backgroundColor: accent,
                  color: theme.headerText,
                  border: "2px solid #111",
                  borderRadius: "6px",
                  aspectRatio: "2.2 / 1",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "0",
                  overflow: "hidden",
                }}
              >
                <span
                  style={{
                    fontFamily: "'Outfit', 'DM Sans', system-ui, sans-serif",
                    fontWeight: 900,
                    fontSize: "3rem",
                    lineHeight: 0.85,
                    letterSpacing: "-0.02em",
                    display: "block",
                    transform: "translateY(-4%)",
                  }}
                >
                  {l}
                </span>
              </div>
            ))}
          </div>
        )}

        <div
          className="grid gap-1.5 relative"
          style={{ gridTemplateColumns: `repeat(${size}, minmax(0, 1fr))` }}
        >
          {cells.map((cell, i) => (
            // i is safe as key: grid positions are fixed, cells never re-order.
            <CellView key={i} cell={cell} theme={theme} freeLabel={freeLabel} fontRange={fontRange} showCaptions={showCaptions} />
          ))}
        </div>
      </div>

      {/* Marketing footer rendered INSIDE the outer themed border so it
          stays visible when players cut out individual cards from a
          multi-card PDF page. Larger / bolder so it remains readable
          even at small physical print sizes. */}
      {footerText ? (
        <div
          style={{
            color: headerTextColor,
            opacity: 1,
            fontSize: "1rem",
            fontWeight: 900,
            textAlign: "center",
            letterSpacing: "0.03em",
            paddingTop: "12px",
            paddingBottom: "4px",
            textTransform: "uppercase",
            fontFamily: "'Outfit', 'DM Sans', system-ui, sans-serif",
          }}
        >
          {footerText}
        </div>
      ) : null}
    </div>
  );
}

function CellView({ cell, theme, freeLabel, fontRange, showCaptions = true }) {
  const baseBg = cell.isFree ? theme.header : "#ffffff";
  const baseColor = cell.isFree ? theme.headerText : "#111";
  const hasImg = !!cell.image;
  const hasText = !!(cell.text && cell.text.trim().length > 0);
  const cellStyleBase = { backgroundColor: baseBg, color: baseColor, border: "2px solid #111", borderRadius: "6px" };

  if (cell.isFree) {
    const fl = (freeLabel || "FREE").toUpperCase();
    return (
      <div
        className="aspect-square flex items-center justify-center font-display font-black text-center overflow-hidden"
        style={{ ...cellStyleBase, padding: "8px" }}
      >
        <AutoFitText
          text={fl}
          maxFontPx={Math.round(fontRange.max * 1.4)}
          minFontPx={fontRange.min}
          style={{ fontWeight: 900, letterSpacing: "0.02em" }}
        />
      </div>
    );
  }

  if (hasImg && hasText && !showCaptions) {
    // Picture-only mode: show the illustration centered (object-contain so the
    // drawing is never cropped) — the label stays on the cell for the call sheet.
    return (
      <div className="aspect-square overflow-hidden flex items-center justify-center p-1.5" style={cellStyleBase}>
        <img src={cell.image} alt={cell.text || ""} className="max-h-full max-w-full object-contain" />
      </div>
    );
  }

  if (hasImg && hasText) {
    return (
      <div className="aspect-square flex flex-col overflow-hidden" style={cellStyleBase}>
        <div className="flex-[3] overflow-hidden p-1 flex items-center justify-center">
          <img src={cell.image} alt={cell.text || ""} className="max-h-full max-w-full object-contain" />
        </div>
        <div
          className="flex-[1] flex items-center justify-center text-center overflow-hidden"
          style={{ padding: "2px 6px" }}
        >
          <AutoFitText
            text={cell.text}
            maxFontPx={Math.round(fontRange.max * 0.7)}
            minFontPx={fontRange.min}
            style={{ fontWeight: 800 }}
          />
        </div>
      </div>
    );
  }

  if (hasImg) {
    return (
      <div className="aspect-square overflow-hidden flex items-center justify-center" style={cellStyleBase}>
        <img src={cell.image} alt={cell.text || ""} className="w-full h-full object-cover" />
      </div>
    );
  }

  return (
    <div
      className="aspect-square flex items-center justify-center text-center overflow-hidden"
      style={{ ...cellStyleBase, padding: "6px" }}
    >
      <MusicOrPlainText text={cell.text || ""} fontRange={fontRange} />
    </div>
  );
}

// Renders music cells as artist (small) above title (large), if the cell
// text uses the "Artist | Title" pipe format. Falls back to plain auto-fit
// rendering otherwise — keeps all existing themes unchanged.
function MusicOrPlainText({ text, fontRange }) {
  const idx = text.indexOf("|");
  if (idx > 0 && idx < text.length - 1) {
    const artist = text.slice(0, idx).trim();
    const title = text.slice(idx + 1).trim();
    return (
      <div className="w-full h-full flex flex-col items-stretch justify-center" style={{ gap: "6px" }}>
        <div className="flex items-center justify-center text-center overflow-hidden" style={{ height: "24%" }}>
          <AutoFitText
            text={artist}
            maxFontPx={Math.max(11, Math.round(fontRange.max * 0.45))}
            minFontPx={Math.max(8, Math.round(fontRange.min * 0.85))}
            style={{ fontWeight: 600, opacity: 0.75, letterSpacing: "0.02em" }}
          />
        </div>
        <div className="flex-1 min-h-0 flex items-center justify-center text-center overflow-hidden">
          <AutoFitText
            text={title}
            maxFontPx={fontRange.max}
            minFontPx={fontRange.min}
            style={{ fontWeight: 900 }}
          />
        </div>
      </div>
    );
  }
  return (
    <AutoFitText
      text={text}
      maxFontPx={fontRange.max}
      minFontPx={fontRange.min}
      style={{ fontWeight: 800 }}
    />
  );
}
